import api from '../utils/api';

const BASE = '/api/users';

/**
 * Get a user by id. Your own id returns your account (with optional
 * population); anyone else's returns their public profile only, and the
 * populate flags are ignored.
 * @route GET /api/users/:userId
 * @param {string} userId
 * @param {{ populateKeys?: boolean, populateAssignments?: boolean }} [params]
 * @returns {Promise<Object>} The account or the public profile
 */
export const getUser = (userId, params = {}) =>
    api.get(`${BASE}/${userId}`, { params }).then((r) => r.data);

/**
 * Get a user by username.
 * @route GET /api/users/username/:username
 * @param {string} username
 * @returns {Promise<Object>} The public profile, or your account if it is yours
 */
export const getUserByUsername = (username) =>
    api.get(`${BASE}/username/${username}`).then((r) => r.data);

/**
 * Get a user by email (admin only).
 * @route GET /api/users/email/:email
 * @param {string} email
 * @returns {Promise<Object>} The user document
 */
export const getUserByEmail = (email) =>
    api.get(`${BASE}/email/${email}`).then((r) => r.data);

/**
 * Get your own sent and received feedback, fully populated (signed-in user only).
 * @route GET /api/users/:userId/feedback
 * @param {string} userId
 * @returns {Promise<{ sentFeedback: Object[], receivedFeedback: Object[] }>}
 */
export const getUserFeedback = (userId) =>
    api.get(`${BASE}/${userId}/feedback`).then((r) => r.data);

/**
 * Get your own joined classrooms, populated with owner usernames (signed-in user only).
 * @route GET /api/users/:userId/joined
 * @param {string} userId
 * @returns {Promise<{ joinedClassrooms: Object[] }>}
 */
export const getJoinedClassrooms = (userId) =>
    api.get(`${BASE}/${userId}/joined`).then((r) => r.data);

/**
 * Update your own profile: `bio`, `categories`, a new `image` upload, or
 * `profilePicture: null` to clear it. The server ignores every other field.
 * Accepts FormData (for a profile-picture upload) or a plain object.
 * @route PATCH /api/users/:userId
 * @param {string} userId  must be the signed-in user
 * @param {FormData|Object} data
 * @returns {Promise<Object>} The updated account
 */
export const updateUser = (userId, data) => {
    const headers = data instanceof FormData ? { 'Content-Type': 'multipart/form-data' } : {};
    return api.patch(`${BASE}/${userId}`, data, { headers }).then((r) => r.data);
};

/**
 * Record where the logged-in user has got to with a guided tutorial.
 *
 * Deliberately not `updateUser`: this writes only the tutorial subtree, and only
 * for whoever the session says you are — the id comes from the cookie, never
 * from the URL.
 *
 * @route PATCH /api/users/me/tutorials/:tutorialId
 * @param {string} tutorialId e.g. 'create-basics'
 * @param {'prompted'|'completed'|'dismissed'} event
 * @returns {Promise<Object>} The updated user document
 */
export const updateMyTutorialStatus = (tutorialId, event) =>
    api.patch(`${BASE}/me/tutorials/${tutorialId}`, { event }).then((r) => r.data);

/**
 * Apply what arriving at a published situation, or picking a published choice,
 * does to the signed-in player's keys. The server reads the keys from that
 * situation or choice; the client never names key ids.
 * @route PATCH /api/users/me/keys/apply
 * @param {{ situationId: string } | { choiceId: string }} source
 * @returns {Promise<{ keys: string[] }>} The keys now held
 */
export const applyMyKeys = (source) =>
    api.patch(`${BASE}/me/keys/apply`, source).then((r) => r.data);

/**
 * Clear the signed-in player's keys for one scenario (starting it over).
 * @route PATCH /api/users/me/keys/clear
 * @param {string} scenarioId
 * @returns {Promise<{ keys: string[] }>} The keys still held
 */
export const clearMyScenarioKeys = (scenarioId) =>
    api.patch(`${BASE}/me/keys/clear`, { scenarioId }).then((r) => r.data);

/**
 * Search users by username (case-insensitive substring), for invite pickers.
 * Signed-in users only.
 * @route GET /api/users/search?username={username}
 * @param {string} username
 * @returns {Promise<{_id: string, username: string, profilePicture?: string}[]>} Up to 20 matches
 */
export const searchUsersByUsername = (username) =>
    api.get(`${BASE}/search`, { params: { username } }).then((r) => r.data);

/**
 * Full-text fuzzy search across users (username/email). Admin only.
 * @route POST /api/users/search-users
 * @param {string} searchTerm
 * @returns {Promise<{ info: Object[] }>}
 */
export const searchUsers = (searchTerm) =>
    api.post(`${BASE}/search-users`, { searchTerm }).then((r) => r.data);

/**
 * Get all users (admin only).
 * @route GET /api/users/get-all-users
 * @returns {Promise<Object[]>}
 */
export const getAllUsers = () =>
    api.get(`${BASE}/get-all-users`).then((r) => r.data);

/**
 * Update a user as an admin (tier-gated server-side).
 * @route PATCH /api/users/admin-update/:userId
 * @param {string} userId
 * @param {Object} updates
 * @returns {Promise<Object>} The updated user document
 */
export const adminUpdateUser = (userId, updates) =>
    api.patch(`${BASE}/admin-update/${userId}`, updates).then((r) => r.data);

/**
 * Check whether a username is available for the logged-in user.
 * @route GET /api/users/settings/check-username?username={username}
 * @param {string} username
 * @returns {Promise<{ available: boolean }>}
 */
export const checkUsernameAvailability = (username) =>
    api.get(`${BASE}/settings/check-username`, { params: { username } }).then((r) => r.data);

/**
 * Change the logged-in user's username (requires current password).
 * @route PATCH /api/users/settings/change-username
 * @param {{ newUsername: string, currentPassword: string }} data
 * @returns {Promise<{ successMessage: string, newUsername: string }>}
 */
export const changeUsername = (data) =>
    api.patch(`${BASE}/settings/change-username`, data).then((r) => r.data);
